import {
  request,
  xhr,
  makeFormData,
} from '@packages/systems/core/utils/ClientUtils';
import {
  AssetSignError,
  AssetUploadError,
  AssetDownloadError,
  AssetPermissionError,
  AssetNotFoundError,
  CsvUploadError,
  CsvSignError,
} from '@packages/systems/assets';
import {isPersisted} from '@packages/systems/core/utils/RecordUtils';
import {withoutThumbDataUrl} from '@packages/shared/utils/AssetUtils';
import {resolveS3UploadUrl} from '@packages/utilities/s3-utils';

export function signFile(file) {
  const path = getPostPath(file);

  return request(path, {
    method: 'POST',
    body: makeSignBody(file),
  })
    .then((response) => ({
      response,
      originalFile: file,
      file: file
        .set('_id', response._id)
        .set('cdnUrl', response.url)
        .set('createdOn', response.createdOn)
        .set('s3Url', response.s3Url),
    }))
    .catch(() => {
      return Promise.reject(new AssetSignError(file));
    });
}

export function uploadFile(file, {accessKey, s3bucket, response, onProgress}) {
  const {policy, key, contentType, signature} = response;

  return xhr({
    method: 'POST',
    host: resolveS3UploadUrl(response.uploadUrl, s3bucket),
    // cors: true suppresses Webflow-only headers (attribution, X-Requested-With) on this
    // direct S3 POST. Without it, AI/MCP attribution headers would be sent to S3, expanding
    // CORS preflight requirements for headers S3 doesn't use and risk blocking the upload.
    cors: true,
    onUploadProgress: (e) => onProgress && onProgress(e.loaded / e.total),
    body: makeFormData({
      policy,
      key,
      'Content-Type': contentType,
      'Cache-Control': 'max-age=31536000, must-revalidate',
      signature,
      success_action_status: 201,
      acl: 'public-read',
      AWSAccessKeyId: accessKey,
      file: file.blob,
    }),
  })
    .then(() => file.set('uploadProgress', null))
    .catch(() => {
      return Promise.reject(new AssetUploadError(file));
    });
}

export function importFile(
  url,
  {
    siteId,
    databaseId,
    fileId,
    figmaNodeId,
    figmaFileKey,
    figmaFileName,
    origFigmaFileName,
    figmaScale,
  }
) {
  const path = getEndpointPath('import-file', {siteId, databaseId});
  return request(path, {
    method: 'POST',
    body: JSON.stringify({
      url,
      fileId,
      figmaNodeId,
      figmaFileKey,
      figmaFileName,
      origFigmaFileName,
      figmaScale,
    }),
  });
}

export function signCsvFile(file, siteId) {
  const path = ['/api/sites', siteId, 'signCsvImport'];

  const body = JSON.stringify({
    fileName: file.name,
  });

  return request(path, {
    method: 'POST',
    body,
  }).catch(() => {
    return Promise.reject(new CsvSignError(file));
  });
}

export function getSignCsvFile(file, siteId) {
  const params = {
    fileName: file.name,
  };

  const path = [
    '/api/sites',
    siteId,
    `signCsvImport?${new URLSearchParams(params)}`,
  ];

  return request(path, {
    method: 'GET',
  }).catch(() => {
    return Promise.reject(new CsvSignError(file));
  });
}

export function uploadCsvFile(file, signatureResponse) {
  return xhr({
    method: 'POST',
    host: resolveS3UploadUrl(
      signatureResponse.uploadUrl,
      signatureResponse.fields.bucket
    ),
    // cors: true suppresses Webflow-only headers (attribution, X-Requested-With) on this
    // direct S3 POST. Without it, AI/MCP attribution headers would be sent to S3, expanding
    // CORS preflight requirements for headers S3 doesn't use and risk blocking the upload.
    cors: true,
    body: makeFormData({
      ...signatureResponse.fields,
      file,
    }),
  }).catch(() => {
    return Promise.reject(new CsvUploadError(file));
  });
}

export function markFileAsDeleted(file) {
  if (!isPersisted(file)) {
    return Promise.resolve();
  }

  return request(getPutPath(file), {
    method: 'PUT',
    body: JSON.stringify({markedAsDeleted: true}),
  }).then(() => file.set('markedAsDeleted', true));
}

export function downloadBlobFromUrl(url) {
  return xhr({
    method: 'GET',
    host: url,
    responseType: 'blob',
    cors: true,
  })
    .then((_xhr) => _xhr.response)
    .catch((e) => {
      //  0 - CORS error status, 401 - Unauthorized, 403 - Forbidden
      if ([0, 401, 403].indexOf(e.status) > -1) {
        return Promise.reject(new AssetPermissionError(url));
      }

      if (e.status === 404) {
        return Promise.reject(new AssetNotFoundError(url));
      }

      return Promise.reject(new AssetDownloadError(url));
    });
}

export function makeSignBody(file) {
  const {fileSize, origFileName, ...rest} = asJSON(file);

  return JSON.stringify({
    ...rest,
    title: origFileName,
    size: fileSize,
    override: false,
  });
}

export function makeSignQuery(file) {
  const fileData = asJSON(file);
  const {fileSize, origFileName} = fileData;

  return {
    ...fileData,
    title: origFileName,
    size: fileSize,
    override: false,
    _id: file._id,
  };
}

function asJSON(file) {
  return withoutThumbDataUrl(file)
    .remove('_id')
    .remove('blob')
    .remove('blobUrl')
    .remove('createdOn')
    .toJSON();
}

function getPostPath(file) {
  return file.siteId
    ? ['/api/sites', file.siteId, 'signedUpload']
    : ['/api/v1/databases', file.databaseId, 'signedUpload'];
}

function getEndpointPath(endpoint, {siteId, databaseId}) {
  return siteId
    ? ['/api/sites', siteId, endpoint]
    : ['/api/v1/databases', databaseId, endpoint];
}

function getPutPath(file) {
  return file.siteId
    ? ['/api/sites', file.siteId, 'files', file._id]
    : ['/api/v1/databases', file.databaseId, 'files', file._id];
}
